import React, {
  ComponentPropsWithRef,
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  KeyboardEvent,
  PropsWithoutRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import styled from '@emotion/styled';
import {
  compose,
  variant,
  space,
  display,
  flex,
  layout,
  DisplayProps,
  FlexProps,
  LayoutProps,
  SpaceProps,
  theme,
} from '@lucid/theme';
import gsap from 'gsap';
import { assignRefs } from '@lucid/utils';

import { Component } from '../../types/types';
import CardAction, { CardActionProps } from './card-action';
import CardActions, { CardActionsProps } from './card-actions';
import CardContent, { CardContentProps } from './card-content';
import CardImage, { CardImageProps } from './card-image';
import CardSubtitle, { CardSubtitleProps } from './card-subtitle';
import CardTitle, { CardTitleProps } from './card-title';
import decorate from '../decorate/decorate';

export interface CardSubComponents {
  /** The Action Subcomponent for Card. */
  Action: FC<CardActionProps>;
  /** The Actions Subcomponent for Card. */
  Actions: FC<CardActionsProps>;
  /** The Content Subcomponent for Card. */
  Content: FC<CardContentProps>;
  /** The Image Subcomponent for Card. */
  Image: FC<CardImageProps>;
  /** The Subtitle Subcomponent for Card. */
  Subtitle: FC<CardSubtitleProps>;
  /** The Title Subcomponent for Card. */
  Title: FC<CardTitleProps>;
}

export interface CardProps
  extends DisplayProps,
    FlexProps,
    LayoutProps,
    SpaceProps,
    Omit<ComponentPropsWithRef<'div'>, 'size' | 'height' | 'width'> {
  /** Sets the HTML tag for the Card component. */
  as?: 'div' | 'section' | 'article';
  /** The Card component children. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the approriate styles if disabled. */
  isDisabled?: boolean;
  /** Passes an onClick function to the Card component. */
  onClick?: () => void;
  /** Passes an onKeyDown function to the Card component. */
  onKeyDown?: (event: KeyboardEvent<Element>) => void;
  /** Sets the role of the Card component. */
  role?: string;
  /** Enables hover animation. */
  shouldAnimateOnHover?: boolean;
  /** Sets the tabIndex for the Box Component. */
  tabIndex?: number;
  /** The available variants for the Card component. */
  variant?: Component.Type['Cards']['Card'] | Component.Type['Cards']['Card'][];
}

const cardStyleProps = compose(space, display, layout, flex);

const StyledCard: FC<CardProps> = styled.div(
  ({ theme, isDisabled }) => [
    {
      label: 'card',
      backgroundColor: theme.colors.white,
      borderRadius: theme.radii.card,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.colors.coolGrey[10],
      overflow: 'hidden',
    },
    isDisabled && {
      borderColor: theme.colors.coolGrey[5],
      pointerEvents: 'none',
    },
  ],
  variant({
    scale: 'cards.card',
    // required to enable the new API
    variants: {
      defaultVariant: {},
    },
  }),
  cardStyleProps,
);

const CardComponent: ForwardRefExoticComponent<PropsWithoutRef<CardProps>> & Partial<CardSubComponents> = forwardRef<
  HTMLDivElement,
  CardProps
>(({ shouldAnimateOnHover = false, children, ...rest }, forwardedRef) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const [hasHover, setHasHover] = useState(false);

  useEffect(() => {
    if (shouldAnimateOnHover && animationRef.current) {
      hasHover
        ? gsap.to(animationRef.current, {
            scale: 1.07,
            zIndex: theme.zIndices.z10,
            duration: 0.667,
            ease: theme.beziers.slowStartEase,
            overwrite: true,
          })
        : gsap.to(animationRef.current, {
            scale: 1,
            zIndex: 0,
            duration: 0.417,
            ease: theme.beziers.fastEndEase,
            overwrite: true,
          });
    }
  }, [hasHover, shouldAnimateOnHover]);

  return (
    <StyledCard
      ref={assignRefs(animationRef, forwardedRef)}
      onMouseEnter={() => setHasHover(true)}
      onMouseLeave={() => setHasHover(false)}
      {...rest}
    >
      {children}
    </StyledCard>
  );
});

CardComponent.Action = CardAction;
CardComponent.Actions = CardActions;
CardComponent.Content = CardContent;
CardComponent.Image = CardImage;
CardComponent.Subtitle = CardSubtitle;
CardComponent.Title = CardTitle;

// manually adding display name or it goes missing on build
CardComponent.displayName = 'Card';
const Card = decorate<CardProps, CardSubComponents>(CardComponent);
export { Card };
export default Card;
